import type { CallerVerificationState, VerifyCallerResponse } from "@gioia365/contracts";
import { Button } from "../design/components/Button";
import { Reason } from "../design/components/Reason";
import { StatusPill } from "../design/components/StatusPill";
import { useT } from "../i18n/locale";
import "./caller-verification.css";

// La verifica di un cellulare presso il fornitore (piano M4, D18): un componente solo, usato dal numero mostrato
// dell'azienda (`routes/platform/organization-telephony.tsx`, Task 14) e dal parco dei cellulari di Team (Task 17).
//
// Il fornitore telefona al cellulare e chi lo tiene risponde e digita sulla tastiera il codice di sei cifre che Gioia365 mostra
// qui; il numero risulta verificato soltanto con la conferma firmata del fornitore, che il server scrive (`state`). Il
// componente non parla con il server e non rilegge niente: chi lo usa gli dà lo stato che ha letto, la risposta alla richiesta
// appena fatta e l'azione di «Verifica», e rilegge lo stato ogni 3 secondi (`TELEPHONY_REFRESH_MS` di `api/queries.ts`) finché
// `isVerifying(state)`.
//
// Il codice si vede soltanto in `instruction`, cioè nella risposta alla richiesta fatta da questa pagina: il server non lo
// conserva e non lo rimanda, quindi una pagina riaperta mentre la verifica è in corso dice soltanto che si aspetta la conferma.

/** La verifica è stata chiesta e il fornitore non ha ancora risposto: chi la usa continua a rileggere. */
export function isVerifying(state: CallerVerificationState | null): boolean {
  return state === "requested";
}

type Props = {
  /** Come sta la verifica di questo numero presso il fornitore; `null` se non è mai stata chiesta (o è di un altro numero). */
  state: CallerVerificationState | null;
  /** Ciò che il server ha risposto alla richiesta appena fatta da questa pagina, con il codice da digitare. */
  instruction: VerifyCallerResponse["verification"] | null;
  /** La richiesta al fornitore è in corso: un solo invio alla volta. */
  busy: boolean;
  onVerify: () => void;
  /** Il motivo scritto per cui la richiesta non è partita o è stata rifiutata. */
  error?: string | null;
  /** «Verifica» spento da chi usa il componente, per esempio finché l'azienda non è attiva. */
  disabled?: boolean;
  /** L'`id` del testo scritto altrove che dice perché «Verifica» è spento: un pulsante spento non lo spiega da solo. */
  describedBy?: string;
};

/** Il testo con il codice in evidenza: il codice si legge e si digita, deve spiccare in mezzo alla frase. */
function sentenceWithCode(text: string, code: string) {
  const parts = text.split(code);
  if (parts.length !== 2) return text;
  return (
    <>
      {parts[0]}
      <strong className="g-caller-verification__code">{code}</strong>
      {parts[1]}
    </>
  );
}

export function CallerVerification({ state, instruction, busy, onVerify, error, disabled = false, describedBy }: Props) {
  const t = useT();
  const verified = state === "verified";
  let progress = null;
  if (state === "requested") {
    if (instruction?.mode === "type_code_on_phone") {
      progress = <p>{sentenceWithCode(t("callerVerification.typeCode", { code: instruction.code }), instruction.code)}</p>;
    } else if (instruction?.mode === "enter_received_code") {
      progress = <p>{t("callerVerification.codeSent")}</p>;
    } else {
      progress = <p>{t("callerVerification.requested")}</p>;
    }
  } else if (state === "failed") {
    progress = <Reason tone="warning">{t("callerVerification.failed")}</Reason>;
  }
  return (
    <div className="g-caller-verification">
      <div className="g-caller-verification__actions">
        {verified ? (
          <StatusPill tone="success" icon="check">
            {t("callerVerification.verified")}
          </StatusPill>
        ) : (
          <Button onClick={onVerify} disabled={busy || disabled} aria-busy={busy ? "true" : undefined} aria-describedby={disabled ? describedBy : undefined}>
            {busy ? t("callerVerification.busy") : t("callerVerification.verify")}
          </Button>
        )}
      </div>
      {/* Sempre nella pagina e vuota finché non serve: uno screen reader annuncia il codice e ogni cambio di stato. */}
      <div className="g-caller-verification__state" role="status">
        {progress}
      </div>
      {error ? (
        <Reason tone="warning" role="alert">
          {error}
        </Reason>
      ) : null}
    </div>
  );
}
